import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PiggyBank, Wallet, TrendingDown, ShoppingCart, Lightbulb, AlertTriangle, CheckCircle2, Tag, Navigation, Pencil } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import client, { money, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import ShoppingHabits from "@/components/ShoppingHabits";

const TIP_ICON = { warning: AlertTriangle, good: CheckCircle2, info: Lightbulb };
const TIP_TONE = { warning: "bg-[#FDF0ED] text-pp-error", good: "bg-[#EBF7EE] text-pp-forest", info: "bg-[#EAF2FB] text-pp-info" };

function Stat({ icon: Icon, label, value, sub, testId, dark }) {
  return (
    <div data-testid={testId} className={`rounded-3xl p-5 pp-shadow ${dark ? "bg-pp-forest text-white" : "bg-white"}`}>
      <Icon className={`h-5 w-5 ${dark ? "text-pp-gold" : "text-pp-forest"}`} />
      <p className="mt-3 text-3xl font-extrabold">{value}</p>
      <p className={`text-sm ${dark ? "text-white/75" : "text-pp-muted"}`}>{label}</p>
      {sub && <p className={`text-xs mt-1 ${dark ? "text-white/60" : "text-pp-muted"}`}>{sub}</p>}
    </div>
  );
}

function Budget({ d, reload }) {
  const [editing, setEditing] = useState(!d.monthly_budget);
  const [val, setVal] = useState(d.monthly_budget || "");
  const save = async () => {
    try { await client.put("/insights/budget", { monthly_budget: +val }); toast.success("Budget saved"); setEditing(false); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const pct = d.monthly_budget ? Math.min(100, (d.this_month_spent / d.monthly_budget) * 100) : 0;
  const over = d.monthly_budget && d.projected_month > d.monthly_budget;
  return (
    <div className="bg-white rounded-3xl p-5 pp-shadow" data-testid="budget-card">
      <div className="flex items-center justify-between">
        <h2 className="font-extrabold text-pp-text flex items-center gap-2"><Wallet className="h-5 w-5 text-pp-forest" /> Monthly grocery budget</h2>
        {!editing && <button onClick={() => setEditing(true)} data-testid="edit-budget" className="text-pp-muted hover:text-pp-forest"><Pencil className="h-4 w-4" /></button>}
      </div>
      {editing ? (
        <div className="mt-3 flex gap-2">
          <Input type="number" value={val} onChange={(e) => setVal(e.target.value)} placeholder="e.g. 800" className="bg-white h-11" data-testid="budget-input" />
          <Button onClick={save} disabled={!val} data-testid="save-budget" className="rounded-full bg-pp-forest h-11">Save</Button>
        </div>
      ) : (
        <>
          <p className="mt-3 text-sm text-pp-muted"><b className="text-2xl text-pp-text">{money(d.this_month_spent)}</b> of {money(d.monthly_budget)} spent this month</p>
          <Progress value={pct} className={`mt-3 h-3 ${over ? "[&>div]:bg-pp-error" : ""}`} data-testid="budget-progress" />
          <p className={`mt-2 text-xs font-semibold ${over ? "text-pp-error" : "text-pp-forest"}`}>
            On pace for {money(d.projected_month)} this month{over ? " — over budget" : " — within budget"}
          </p>
        </>
      )}
    </div>
  );
}

export default function Insights() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const load = () => client.get("/insights").then((r) => setD(r.data)).catch(() => setD({}));
  useEffect(() => { load(); }, []);

  if (!d) return <div className="space-y-4"><Skeleton className="h-40 rounded-3xl" /><Skeleton className="h-64 rounded-3xl" /></div>;

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-3xl bg-pp-forest text-white p-6 sm:p-8 pp-shadow-lg pp-fade-up" data-testid="savings-hero">
        <img src="/hero-groceries.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative">
          <p className="text-white/75 text-sm font-bold uppercase tracking-wider">Your savings with Pantry Pilot</p>
          <p className="mt-2 text-5xl sm:text-6xl font-extrabold text-pp-gold" data-testid="lifetime-saved">{money(d.lifetime_saved)}</p>
          <p className="text-white/85 mt-1">saved across {d.trips_count} trip{d.trips_count === 1 ? "" : "s"} · {money(d.month_saved)} in the last 30 days</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={PiggyBank} label="Saved this week" value={money(d.week_saved)} testId="stat-week-saved" dark />
        <Stat icon={ShoppingCart} label="Average basket" value={money(d.avg_basket)} testId="stat-avg-basket" />
        <Stat icon={TrendingDown} label="Spent this month" value={money(d.this_month_spent)} testId="stat-month-spent" />
        <Stat icon={Navigation} label="Active weeks (last 8)" value={`${d.active_weeks_last_8}/8`} testId="stat-active-weeks" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-3xl p-5 pp-shadow" data-testid="spend-chart">
            <h2 className="font-extrabold text-pp-text">Spending vs. savings by month</h2>
            <div className="h-64 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.months || []} margin={{ left: -16, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2EAD8" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: "#66756F", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#66756F", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#FFF9EE" }} />
                  <Bar dataKey="spent" name="Spent" fill="#075B3A" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="saved" name="Saved" fill="#F5BE41" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white rounded-3xl p-5 pp-shadow" data-testid="category-breakdown">
            <h2 className="font-extrabold text-pp-text">Where your grocery money goes</h2>
            <div className="mt-4 space-y-3">
              {(d.categories || []).map((c) => (
                <div key={c.category}>
                  <div className="flex justify-between text-sm"><span className="font-semibold text-pp-text">{c.category}</span><span className="text-pp-muted">{money(c.spent)} · {c.pct}%</span></div>
                  <div className="mt-1 h-2.5 rounded-full bg-pp-cream overflow-hidden"><div className="h-full rounded-full bg-pp-fresh" style={{ width: `${c.pct}%` }} /></div>
                </div>
              ))}
              {!d.categories?.length && <p className="text-sm text-pp-muted">Finish a trip or scan a receipt to see your category breakdown.</p>}
            </div>
          </div>
        </div>
        <div className="space-y-6">
          <Budget d={d} reload={load} />
          <ShoppingHabits />
          <div className="bg-white rounded-3xl p-5 pp-shadow space-y-2.5" data-testid="savings-tips">
            <h2 className="font-extrabold text-pp-text">Tips to save more</h2>
            {(d.tips || []).map((t, i) => {
              const Icon = TIP_ICON[t.kind] || Lightbulb;
              return <div key={i} className={`flex gap-2 rounded-2xl p-3 text-sm ${TIP_TONE[t.kind]}`}><Icon className="h-4 w-4 shrink-0 mt-0.5" /><span className="text-pp-text">{t.text}</span></div>;
            })}
          </div>
          {d.deals_on_your_items?.length > 0 && (
            <div className="bg-white rounded-3xl p-5 pp-shadow" data-testid="deals-on-your-items">
              <h2 className="font-extrabold text-pp-text flex items-center gap-2"><Tag className="h-5 w-5 text-pp-gold" /> On sale: things you buy</h2>
              <div className="mt-3 space-y-2">
                {d.deals_on_your_items.map((p) => (
                  <div key={p.product_name} className="flex items-center gap-3">
                    <img src={p.image} alt="" className="h-11 w-11 rounded-xl object-contain bg-white" />
                    <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-pp-text truncate">{p.product_name}</p><p className="text-xs text-pp-muted truncate">{p.store_name}</p></div>
                    <div className="text-right"><p className="font-extrabold text-pp-forest">{money(p.sale_price)}</p><p className="text-[11px] text-pp-muted line-through">{money(p.regular_price)}</p></div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Button onClick={() => navigate("/list")} data-testid="savings-plan-next" className="w-full h-12 rounded-full bg-pp-forest font-bold">Plan my next trip</Button>
        </div>
      </div>
    </div>
  );
}
